import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { apiRequestResponseMsg, apiStatus } from '@va/constants';

import { changeEmail } from '@va/standalone/shared/actions';
import './ChangeEmail.scss';

import { resetRequestStatus } from '@va/dashboard/actions/api';
import { closeModal } from '@va/dashboard/actions/ui';
import { isModalOpen as isModalOpenSelector } from '@va/dashboard/selectors/ui';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { getApiRequestErrorMessage, getApiRequestStatus } from '@va/standalone/shared/selectors';
import { Button, ModalHeader } from '@va/ui/design-system';
import { ModalWrapper } from '@va/util/components';
import ChangeEmailForm, { CHANGE_EMAIL_FORM_ID } from './ChangeEmailForm';
import { EmailFormFieldNames, EmailFormValues, useChangeEmailForm } from './useChangeEmailForm';

const ChangeEmailContainer = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const { showSuccessNotification } = useAddNotification();
  const isModalOpen = useSelector((state) => isModalOpenSelector(state, 'changeEmail'));
  const changeEmailStatus = useSelector((state) => getApiRequestStatus(state, 'changeEmail'));
  const changeEmailErrorMessage = useSelector((state) => getApiRequestErrorMessage(state, 'changeEmail'));

  const onCloseModal = useCallback(() => {
    if (changeEmailStatus === apiStatus.IN_PROGRESS) {
      return;
    }

    dispatch(resetRequestStatus('changeEmail'));
    dispatch(closeModal('changeEmail'));
  }, [changeEmailStatus, dispatch]);

  const onSubmit = useCallback(
    (data: EmailFormValues) => {
      dispatch(changeEmail(data));
    },
    [dispatch],
  );

  const form = useChangeEmailForm(onSubmit);
  const { setFieldError } = form;

  useEffect(() => {
    if (changeEmailStatus !== apiStatus.FAILED) return;

    if (changeEmailErrorMessage === apiRequestResponseMsg.ALREADY_EXISTING_USER) {
      setFieldError(EmailFormFieldNames.Email, 'modal.changeEmail.emailRequest.alreadyExistingUserError');
      return;
    }

    setFieldError(EmailFormFieldNames.Email, 'form.generalError');
  }, [changeEmailErrorMessage, changeEmailStatus, setFieldError]);

  useEffect(() => {
    if (changeEmailStatus !== apiStatus.SUCCEEDED) return;
    showSuccessNotification();

    onCloseModal();
  }, [changeEmailStatus, onCloseModal, showSuccessNotification]);

  return (
    <ModalWrapper className='change-email-wrapper' closeModal={onCloseModal} isModalOpen={isModalOpen}>
      <div className='min-w-125'>
        <ModalHeader onClose={onCloseModal} title={translate('modal.changeEmail.newEmail.title')}></ModalHeader>
        <ChangeEmailForm form={form} />
        <div className='p-8u'>
          <Button
            form={CHANGE_EMAIL_FORM_ID}
            type='submit'
            disabled={!form.isValid || changeEmailStatus === apiStatus.IN_PROGRESS}
            color='primary'
            className='ml-auto'
            text={translate('button.changeEmail')}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default ChangeEmailContainer;
